import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useT from '../../../i18n';

/**
 * FAQ — Fase 2.5 addition. Custom editorial accordion on bg-paper-cool.
 *
 * - Hairline dividers, no card chrome
 * - Multiple items can be open simultaneously (generous editorial pattern)
 * - Height-auto animation with Framer AnimatePresence (0.35s ease-out)
 * - Reuses legacy `t.faq.items[]` (6 items pre-localised in NO + EN)
 * - New headings/eyebrow live under `landingV2.faq.*` for editorial voice
 */
export default function FAQ() {
  const t = useT();
  const s = t.landingV2.faq;
  const items = t.faq?.items || [];
  const [openIndices, setOpenIndices] = useState(new Set());

  const toggle = (i) => {
    setOpenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  return (
    <section
      className="bg-paper-cool py-section border-t border-ink/[0.06]"
      data-testid="landing-faq"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <p className="font-sans text-xs uppercase tracking-widest text-ink/50">
            {s.eyebrow}
          </p>
          <h2 className="mt-6 font-display font-light text-display-md text-ink">
            {s.heading}
          </h2>
        </motion.div>

        <div className="mt-16" data-testid="faq-accordion">
          {items.map((item, i) => {
            const isOpen = openIndices.has(i);
            return (
              <div
                key={i}
                className="border-b border-ink/10"
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-4 focus-visible:ring-offset-paper-cool rounded-sm"
                  data-testid={`faq-question-${i}`}
                >
                  <span className="font-display text-xl text-ink leading-snug">
                    {item.q}
                  </span>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    aria-hidden
                    className="shrink-0 text-ink/40 font-sans text-2xl leading-none select-none"
                  >
                    +
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      id={`faq-answer-${i}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p
                        className="pb-6 pr-12 font-sans text-sm text-ink/70 max-w-prose-wide leading-relaxed"
                        data-testid={`faq-answer-text-${i}`}
                      >
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
